/**
 * Orders FSM Types and Constants
 *
 * Этот файл содержит все типы, статусы и константы для FSM заказов.
 * Структура данных соответствует backend API контракту.
 *
 * FSM = единственный источник бизнес-логики для UI.
 */

// ============================================================================
// BACKEND STATUSES (from API)
// ============================================================================

/**
 * Статусы оффера/заявки (Offer)
 * Backend: OfferResponse.status
 * @readonly
 * @enum {string}
 */
export const OfferStatus = Object.freeze({
  PENDING: "pending",
  APPROVED: "approved",
  DECLINED: "declined",
})

/**
 * Статусы сделки (Deal)
 * Backend: DealCompositeResponse.status
 * @readonly
 * @enum {string}
 */
export const DealStatus = Object.freeze({
  ACTIVE: "active",
  CANCELED: "canceled",
  FINISHED: "finished",
})

/**
 * Типы состояний сделки (DealState.type)
 * Backend: DealState.type
 *
 * ВАЖНО: Контракт использует "auth" вместо "authentication"
 *
 * @readonly
 * @enum {string}
 */
export const DealStateType = Object.freeze({
  OFFER: "offer",
  PAYMENT: "payment",
  DELIVERY: "delivery",
  AUTH: "auth", // Контракт: "auth" не "authentication"
  PICKUP: "pickup",
  FINISHED: "finished",
  REFUND: "refund",
  REFUND_DELIVERY: "refund_delivery",
  REFUND_FINISHED: "refund_finished",
})

/**
 * Статусы состояний сделки (DealState.status)
 * Backend: DealState.status
 * @readonly
 * @enum {string}
 */
export const DealStateStatus = Object.freeze({
  PENDING: "pending",
  SUCCESS: "success",
  FAILED: "failed",
  CANCELLED: "cancelled",
  EXPIRED: "expired",
  DELAYED: "delayed",
  ACTION_REQUIRED: "action_required",
  TECHNICAL_FAILED: "technical_failed",
  STORAGE: "storage",
  REFUNDED: "refunded",
})

/**
 * Типы пользователей
 * @readonly
 * @enum {string}
 */
export const UserType = Object.freeze({
  SELLER: "seller",
  RESELLER: "reseller",
})

/**
 * Инициаторы отмены
 * Backend: cancel.actor
 * @readonly
 * @enum {string}
 */
export const CancelActor = Object.freeze({
  SELLER: "seller",
  RESELLER: "reseller",
  SYSTEM: "system",
})

/**
 * Причины отмены сделки (из контракта)
 * Backend: cancel.reason
 * @readonly
 * @enum {string}
 */
export const CancelReason = Object.freeze({
  /** Товара нет в наличии */
  NO_STOCK: "no_stock",
  /** Не подходит цена */
  PRICE_NOT_OK: "price_not_ok",
  /** Доставка слишком долгая */
  DELIVERY_TOO_LONG: "delivery_too_long",
  /** По иной причине */
  OTHER: "other",
})

/**
 * Причины отклонения запроса на продление (reseller)
 * По спецификации: использует DealCancelReason
 * @readonly
 * @enum {string}
 */
export const ExtendRejectReason = Object.freeze({
  NO_STOCK: "no_stock",
  PRICE_NOT_OK: "price_not_ok",
  DELIVERY_TOO_LONG: "delivery_too_long",
  OTHER: "other",
})

/**
 * Системные причины отмены (UI-level, derived из FSM)
 *
 * НЕ передаётся в API! Используется только для UI-текстов.
 * Выводится из DealState.type + DealState.status через deriveSystemReason()
 *
 * @readonly
 * @enum {string}
 */
export const OrderSystemReason = Object.freeze({
  /** Истекло время на принятие заявки (offer.expired) */
  REQUEST_TIMEOUT: "request_timeout",
  /** Истекло время на оплату (payment.expired) */
  PAYMENT_TIMEOUT: "payment_timeout",
  /** Истекло время доставки (delivery.expired) */
  DELIVERY_TIMEOUT: "delivery_timeout",
  /** Товар не прошёл аутентификацию (auth.failed) */
  AUTHENTICATION_FAILED: "authentication_failed",
  /** Техническая ошибка (*.technical_failed) */
  TECHNICAL_ISSUE: "technical_issue",
  /** Реселлер отклонил запрос на продление (delivery.failed после extend request) */
  EXTENSION_REJECTED: "extension_rejected",
  /** Оплата не прошла (payment.failed) */
  PAYMENT_FAILED: "payment_failed",
  /** Оплата отменена / технические проблемы (payment.cancelled) */
  PAYMENT_CANCELLED: "payment_cancelled",
})

// ============================================================================
// UI STATES (FSM output)
// ============================================================================

/**
 * UI состояния заказа (вычисляются FSM на основе states[])
 *
 * Маппинг backend → UI:
 * - offer.pending → PENDING_REQUEST
 * - offer.declined → REQUEST_REJECTED
 * - payment.pending → WAITING_PAYMENT
 * - delivery.pending → WAITING_SELLER_READY
 * - delivery.success (in transit) → SELLER_WORKING
 * - auth.pending/success → IN_AUTHENTICATION
 * - pickup.pending → READY_FOR_PICKUP
 * - finished.success → COMPLETED
 * - deal.canceled → CANCELLED_*
 *
 * @readonly
 * @enum {string}
 */
export const OrderUIState = Object.freeze({
  /** offer.status = pending */
  PENDING_REQUEST: "pending_request",

  /** offer.status = declined (селлер отклонил заявку) */
  REQUEST_REJECTED: "request_rejected",

  /** offer.status = declined (реселлер отклонил торг) */
  BID_REJECTED: "bid_rejected",

  /** offer.approved + payment.pending */
  WAITING_PAYMENT: "waiting_payment",

  /** payment.success + delivery.pending */
  WAITING_SELLER_READY: "waiting_seller_ready",

  /** Seller запросил продление дедлайна */
  EXTEND_REQUESTED: "extend_requested",

  /** delivery.success (товар в пути) */
  SELLER_WORKING: "seller_working",

  /** auth.pending | auth.success (на проверке) */
  IN_AUTHENTICATION: "in_authentication",

  /** pickup.pending (товар готов к выдаче) */
  READY_FOR_PICKUP: "ready_for_pickup",

  /** pickup.delayed (время получения увеличено) */
  PICKUP_DELAYED: "pickup_delayed",

  /** refund.pending (оформлен возврат) */
  RETURN_REQUESTED: "return_requested",

  /** refund_delivery (возврат в процессе доставки) */
  REFUND_IN_DELIVERY: "refund_in_delivery",

  /** refund_finished (возврат завершён) */
  REFUND_COMPLETED: "refund_completed",

  /** refund.success (товар возвращён селлеру) */
  REFUND_RETURNED_TO_SELLER: "refund_returned_to_seller",

  /** delivery.delayed (задержка доставки) */
  DELIVERY_DELAYED: "delivery_delayed",

  /** state.storage (платное хранение) */
  PAID_STORAGE: "paid_storage",

  /** pickup.storage: селлер — «Сделка закрыта», реселлер — «Платное хранение» */
  PICKUP_STORAGE: "pickup_storage",

  /** state.technical_failed (техническая ошибка) */
  TECHNICAL_ERROR: "technical_error",

  /** Seller получил товар обратно после pickup */
  PICKED_UP_BY_RESELLER: "picked_up_by_reseller",

  /** Seller ожидает оплату (после успешной сделки) */
  PAYMENT_REQUIRED: "payment_required",

  /** deal.finished */
  COMPLETED: "completed",

  /**
   * deal.active, последний delivery в states — failed (автоотмена: не доставлен вовремя).
   * Не путать с deal.canceled — отдельный экран «сделка закрыта» по таймауту доставки.
   */
  DELIVERY_TIMEOUT_AUTO_CLOSED: "delivery_timeout_auto_closed",

  /** deal.canceled (by seller) */
  CANCELLED_BY_SELLER: "cancelled_by_seller",

  /** deal.canceled (by reseller) */
  CANCELLED_BY_RESELLER: "cancelled_by_reseller",

  /** deal.canceled (by system - timeout, auth failed, etc) */
  CANCELLED_BY_SYSTEM: "cancelled_by_system",

  /** Fallback для обратной совместимости */
  CANCELLED: "cancelled",

  /** Пользователь сообщил о проблеме */
  PROBLEM_REPORTED: "problem_reported",

  /** Селлер сделал встречное предложение по цене (торги) */
  BID_PENDING: "bid_pending",
})

// ============================================================================
// EVENTS (FSM input)
// ============================================================================

/**
 * События FSM (действия пользователя)
 * @readonly
 * @enum {string}
 */
export const OrderEvent = Object.freeze({
  /** Reseller оплачивает заказ */
  PAY: "PAY",
  /** Seller принимает заявку */
  ACCEPT_REQUEST: "ACCEPT_REQUEST",
  /** Seller отклоняет заявку */
  REJECT_REQUEST: "REJECT_REQUEST",
  /** Reseller отменяет заявку */
  CANCEL_REQUEST: "CANCEL_REQUEST",
  /** Seller обновляет состояние сделки */
  UPDATE_STATE: "UPDATE_STATE",
  /** Reseller подтверждает получение */
  CONFIRM_COMPLETE: "CONFIRM_COMPLETE",
  /** Отмена сделки */
  CANCEL_DEAL: "CANCEL_DEAL",
  /** Seller запрашивает продление дедлайна */
  EXTEND_DEADLINE: "EXTEND_DEADLINE",
  /** Reseller принимает запрос на продление */
  ACCEPT_EXTEND: "ACCEPT_EXTEND",
  /** Reseller отклоняет запрос на продление */
  REJECT_EXTEND: "REJECT_EXTEND",
  /** Сообщить о проблеме с заказом */
  REPORT_PROBLEM: "REPORT_PROBLEM",
  /** Seller подтверждает готовность к отправке */
  CONFIRM_SELLER_READY: "CONFIRM_SELLER_READY",
  /** Seller привязывает карту для получения оплаты */
  LINK_CARD: "LINK_CARD",
  /** Reseller оставляет отзыв о сделке */
  LEAVE_FEEDBACK: "LEAVE_FEEDBACK",
  /** Reseller возвращает товар */
  RETURN_PRODUCT: "RETURN_PRODUCT",
  /** Reseller предлагает свою цену (торги) */
  PROPOSE_PRICE: "PROPOSE_PRICE",
  /** Reseller принимает встречное предложение селлера (торги) */
  ACCEPT_BID: "ACCEPT_BID",
  /** Reseller отклоняет встречное предложение селлера (торги) */
  REJECT_BID: "REJECT_BID",
})

/**
 * Порядок событий для UI (фиксированный)
 * Используется в getAvailableEvents для консистентного порядка кнопок
 */
export const EVENT_ORDER = Object.freeze([
  OrderEvent.ACCEPT_REQUEST,
  OrderEvent.REJECT_REQUEST,
  OrderEvent.PAY,
  OrderEvent.UPDATE_STATE,
  OrderEvent.CONFIRM_COMPLETE,
  OrderEvent.CONFIRM_SELLER_READY,
  OrderEvent.LINK_CARD,
  OrderEvent.LEAVE_FEEDBACK,
  OrderEvent.RETURN_PRODUCT,
  OrderEvent.ACCEPT_EXTEND,
  OrderEvent.REJECT_EXTEND,
  OrderEvent.EXTEND_DEADLINE,
  OrderEvent.PROPOSE_PRICE,
  OrderEvent.ACCEPT_BID,
  OrderEvent.REJECT_BID,
  OrderEvent.CANCEL_REQUEST,
  OrderEvent.CANCEL_DEAL,
  OrderEvent.REPORT_PROBLEM,
])

// ============================================================================
// JSDOC TYPE DEFINITIONS (по контракту)
// ============================================================================

/**
 * ProductModel — модель товара
 * @typedef {Object} ProductModel
 * @property {number} id - ID модели
 * @property {string|null} article - Артикул
 * @property {string|null} title - Название
 * @property {string[]|null} images - Массив URL изображений
 * @property {"MALE"|"FEMALE"|"UNISEX"|"TEENAGER"|null} category - Категория
 * @property {string|null} brand - Бренд
 * @property {string|null} colour - Цвет
 * @property {string|null} date_added - Дата добавления
 * @property {string|null} date_modified - Дата изменения
 */

/**
 * OfferItem — позиция в заявке
 * @typedef {Object} OfferItem
 * @property {number} product_id - ID товара
 * @property {string} price - Цена (string по контракту)
 * @property {string|null} size - Размер
 * @property {string|null} size_type - Тип размера (US, EU, etc)
 * @property {number} count - Количество
 * @property {string|null} trade_price - Цена торга
 * @property {string|null} avg_price - Средняя цена
 * @property {string|null} commission_percent - Процент комиссии
 * @property {string|null} commission_amount - Сумма комиссии
 * @property {ProductModel|null} model - Модель товара (может быть вложена)
 */

/**
 * DealState — этап сделки
 *
 * Каждый state может содержать state-specific payload данные:
 * - delivery: delivery_address
 * - pickup: pickup_code
 *
 * @typedef {Object} DealState
 * @property {number} id - ID состояния
 * @property {string} type - Тип: offer | payment | delivery | auth | pickup | finished | refund | refund_delivery | refund_finished
 * @property {string} status - Статус: pending | success | failed | cancelled | delayed | technical_failed | expired | storage | refunded
 * @property {string} created_at - Время создания
 * @property {string|null} ended_at - Время завершения
 * @property {number} spent_pending_sec - Время в pending (секунды)
 * @property {number} spent_success_sec - Время в success (секунды)
 * @property {number} spent_failed_sec - Время в failed (секунды)
 * @property {string|null} expires_at - Время истечения (дедлайн этапа)
 * @property {string|null} [delivery_address] - Адрес доставки (для delivery state)
 * @property {string|null} [pickup_code] - Код получения (для pickup state)
 * @property {string|null} [working_hours] - Часы работы пункта выдачи (для delivery/pickup state)
 * @property {number|null} [lat] - Широта пункта выдачи
 * @property {number|null} [lng] - Долгота пункта выдачи
 */

/**
 * DealHistoryItem — элемент истории изменений
 * @typedef {Object} DealHistoryItem
 * @property {number} id - ID записи
 * @property {string} changed_at - Время изменения (ISO datetime)
 * @property {string|null} label - Человеческий текст, подготовленный бэком
 * @property {boolean} [is_visible] - Показывать в истории (только true отображаются)
 */

/**
 * DealCancel — информация об отмене
 * @typedef {Object} DealCancel
 * @property {number} id - ID записи об отмене
 * @property {number} deal_id - ID сделки
 * @property {string} actor - Кто отменил: seller | reseller | system
 * @property {string} reason - Причина: no_stock | price_not_ok | delivery_too_long | other
 * @property {string|null} comment - Комментарий
 * @property {string|null} suggested_price - Предложенная цена (для price_not_ok)
 * @property {string} state_type - Тип состояния при отмене (DealStateType)
 * @property {string} created_at - Время отмены
 */

/**
 * DeliveryExtendRequest — запрос на продление доставки (command)
 * @typedef {Object} DeliveryExtendRequest
 * @property {number} extra_hours - Дополнительные часы (1-48)
 */

/**
 * DeliveryExtendReject — отклонение запроса на продление
 * @typedef {Object} DeliveryExtendReject
 * @property {string} reason - Причина из DealCancelReason
 * @property {string|null} comment - Комментарий
 */

/**
 * DealFeedback — отзыв по сделке
 * Используется после finished, влияет на User.rating
 *
 * @typedef {Object} DealFeedback
 * @property {number} deal_id - ID сделки
 * @property {string} comment - Комментарий (обязательный, max 500)
 * @property {number|null} rating - Оценка 1-5 (опционально)
 */

/**
 * Offer — заявка
 * @typedef {Object} Offer
 * @property {number} id - ID заявки
 * @property {number} reseller_id - ID реселлера
 * @property {number} seller_id - ID продавца
 * @property {string} status - pending | approved | declined
 * @property {string} created_at - Время создания
 * @property {number|null} deal_id - ID сделки (если создана)
 * @property {string} total_amount - Общая сумма (string по контракту)
 * @property {string} commission_percent - Процент комиссии
 * @property {string} commission_amount - Сумма комиссии
 * @property {OfferItem[]} items - Позиции заявки
 */

/**
 * Deal — сделка (краткая версия в списке)
 * @typedef {Object} Deal
 * @property {number} id - ID сделки
 * @property {number} offer_id - ID заявки
 * @property {number} reseller_id - ID реселлера
 * @property {number} seller_id - ID продавца
 * @property {string} status - active | canceled | finished
 * @property {boolean} is_trading_complete - Флаг завершения торгов (true = торги завершены)
 * @property {string} created_at - Время создания
 * @property {string} updated_at - Время обновления
 */

/**
 * OfferWithModelResponse — основной объект списка заказов (OrderItem)
 *
 * ВАЖНО: states[] и cancel на верхнем уровне, НЕ внутри deal!
 *
 * @typedef {Object} OfferWithModelResponse
 * @property {Offer} offer - Заявка
 * @property {ProductModel|null} model - Модель товара (на верхнем уровне!)
 * @property {Deal|null} deal - Сделка (если создана)
 * @property {DealState[]} states - Текущие состояния (FSM читает отсюда!)
 * @property {DealHistoryItem[]} state_history - История изменений
 * @property {DealCancel|null} cancel - Информация об отмене
 * @property {boolean} is_trading_complete - Флаг завершения торгов
 */

/**
 * DealCompositeResponse — полная информация о сделке (read-model)
 * Используется только для чтения (страницы сделки, таймлайн, UI)
 *
 * @typedef {Object} DealCompositeResponse
 * @property {number} id - ID сделки
 * @property {number} offer_id - ID заявки
 * @property {number} reseller_id - ID реселлера
 * @property {number} seller_id - ID продавца
 * @property {string} status - active | canceled | finished
 * @property {string} created_at - Время создания
 * @property {string} updated_at - Время обновления
 * @property {Object} reseller - Информация о покупателе (User)
 * @property {Object} seller - Информация о продавце (User)
 * @property {DealState[]} states - Текущие состояния FSM
 * @property {DealHistoryItem[]} state_history - История изменений
 * @property {DealCancel|null} cancel - Информация об отмене
 */

// ============================================================================
// LEGACY ALIASES (для обратной совместимости)
// ============================================================================

/** @deprecated Use OfferStatus instead */
export const RequestStatus = Object.freeze({
  NEW: "pending",
  ACCEPTED: "approved",
  REJECTED: "declined",
  CANCELLED: "cancelled",
})

/** @deprecated Use DealStateStatus instead */
export const PaymentStatus = Object.freeze({
  UNPAID: "pending",
  PAID: "success",
  REFUNDED: "refunded",
})

/** @deprecated Use CancelReason instead */
export const DeclineReason = Object.freeze({
  OUT_OF_STOCK: "no_stock",
  PRICE_MISMATCH: "price_not_ok",
  DELIVERY_ISSUE: "delivery_too_long",
  OTHER: "other",
})

/** @deprecated Use DeclineReason instead */
export const RejectReason = DeclineReason

/** @deprecated Use OrderEvent.UPDATE_STATE instead */
export const CONFIRM_SELLER_READY = OrderEvent.UPDATE_STATE
